'use client'

import { Mail, Phone, MapPin, Clock, ArrowRight, ArrowUp, Shield, Users, Building } from 'lucide-react'
import Link from 'next/link'
import { usePathname } from 'next/navigation'
import { useAuth } from '@/contexts/AuthContext'
import { useEffect, useState } from 'react'
import DynamicCompanyInfo from '@/components/DynamicCompanyInfo'
import { motion } from 'framer-motion'

export default function Footer() {
  const pathname = usePathname()
  const [isCRMPage, setIsCRMPage] = useState(false)
  let user = null;
  
  try {
    const authContext = useAuth();
    user = authContext.user;
  } catch (error) {}
  
  useEffect(() => {
    setIsCRMPage(pathname?.startsWith('/crm') || false)
  }, [pathname])

  const currentYear = new Date().getFullYear()

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }

  const crmFooterSections = [
    { title: 'CRM Management', links: [
      { name: 'Dashboard', href: '/crm/dashboard' },
      { name: 'Employees', href: '/crm/employees' },
      { name: 'Clients', href: '/crm/clients' },
      { name: 'Projects', href: '/crm/projects' },
      { name: 'Tasks', href: '/crm/tasks' },
      { name: 'Attendance', href: '/crm/attendance' }
    ]},
    { title: 'Operations', links: [
      { name: 'Reports', href: '/crm/reports' },
      { name: 'Analytics', href: '/crm/dashboard#analytics' },
      { name: 'Revenue', href: '/crm/reports#revenue' },
      { name: 'Payroll', href: '/crm/payroll' },
      { name: 'Leaves', href: '/crm/leaves' },
    ]},
    { title: 'Quick Links', links: [
      { name: 'Add Employee', href: '/crm/employees?action=add' },
      { name: 'New Project', href: '/crm/projects?action=create' },
      { name: 'Add Client', href: '/crm/clients?action=add' },
      { name: 'Settings', href: '/crm/settings' },
    ]},
  ]

  const websiteFooterSections = [
    { title: 'Solutions', links: [
      { name: 'Hospital Management', href: '/solutions/hospital-management' },
      { name: 'School Management', href: '/solutions/school-management' },
      { name: 'Ecommerce Platform', href: '/solutions/ecommerce-platform' },
      { name: 'LMS Platform', href: '/solutions/lms' },
      { name: 'CRM System', href: '/solutions/crm-system' },
      { name: 'All Solutions', href: '/solutions' },
    ]},
    { title: 'Services', links: [
      { name: 'Mobile App Development', href: '/services/mobile-app-development' },
      { name: 'Web Development', href: '/services/web-development' },
      { name: 'AI & ML Services', href: '/services/ai-ml-services' },
      { name: 'DevOps & Cloud', href: '/services/devops-cloud' },
      { name: 'Blockchain', href: '/services/blockchain-development' },
      { name: 'All Services', href: '/services' },
    ]},
    { title: 'Company', links: [
      { name: 'About Us', href: '/about' },
      { name: 'Our Team', href: '/about/team' },
      { name: 'Careers', href: '/about/career' },
      { name: 'Blog', href: '/blog' },
      { name: 'Recruitment', href: '/recruitment' },
      { name: 'Contact', href: '/contact' },
    ]},
    { title: 'Programs', links: [
      { name: 'Training & Placement', href: '/training-placement' },
      { name: 'Career Consulting', href: '/career-consulting' },
      { name: 'Testimonials', href: '/about/testimonials' },
    ]},
  ]

  const footerSections = isCRMPage ? crmFooterSections : websiteFooterSections

  return (
    <footer className="relative bg-gradient-to-b from-slate-900 to-slate-950 text-white overflow-hidden">
      {/* Top wave separator */}
      <div className="absolute top-0 left-0 w-full overflow-hidden leading-none">
        <svg className="relative block w-full h-12" viewBox="0 0 1200 50" preserveAspectRatio="none">
          <path d="M0,25 C300,50 600,0 900,25 C1050,37.5 1150,12.5 1200,25 L1200,0 L0,0 Z" fill="rgb(248 250 252)" />
        </svg>
      </div>

      {/* Glow effects */}
      <div className="absolute top-20 left-1/4 w-64 h-64 bg-sky-500/5 rounded-full blur-3xl" />
      <div className="absolute bottom-20 right-1/4 w-48 h-48 bg-blue-500/5 rounded-full blur-3xl" />

      <div className="container mx-auto px-4 sm:px-6 pt-20 pb-8 relative z-10">
        {/* Main Content */}
        <div className="grid sm:grid-cols-2 lg:grid-cols-6 gap-8 sm:gap-10 mb-12">
          {/* Company Info */}
          <div className="sm:col-span-2 lg:col-span-2">
            <div className="flex items-center mb-5">
              <DynamicCompanyInfo type="logo" variant="light" className="max-h-10 w-auto">
                {(companyInfo: any) => (
                  companyInfo?.logo?.light ? (
                    <img src={companyInfo.logo.light} alt={companyInfo.companyName || 'Logo'} className="max-h-10 w-auto object-contain" />
                  ) : (
                    <div className="flex items-center gap-2">
                      <div className="w-9 h-9 bg-gradient-to-br from-sky-400 to-blue-600 rounded-xl flex items-center justify-center shadow-lg shadow-sky-500/20">
                        <span className="text-white font-bold text-sm">{companyInfo?.companyName?.substring(0, 2).toUpperCase() || 'CO'}</span>
                      </div>
                      <span className="text-lg font-bold"><DynamicCompanyInfo type="companyName" /></span>
                    </div>
                  )
                )}
              </DynamicCompanyInfo>
            </div>
            
            <DynamicCompanyInfo type="companyName">
              {(companyInfo: any) => (
                <p className="text-slate-400 text-sm leading-relaxed mb-5">
                  {isCRMPage 
                    ? 'Comprehensive office management solution for streamlined operations and productivity.'
                    : (companyInfo?.businessInfo?.description || 'Ready-made industry solutions, custom development, training, and expert consulting for digital transformation.')}
                </p>
              )}
            </DynamicCompanyInfo>

            {/* Contact Info */}
            {!isCRMPage ? (
              <div className="space-y-2.5">
                <div className="flex items-center gap-3 text-sm">
                  <Mail size={14} className="text-sky-400" />
                  <DynamicCompanyInfo type="email">
                    {(companyInfo: any) => <span className="text-slate-400">{companyInfo?.contact?.email || 'hello@company.com'}</span>}
                  </DynamicCompanyInfo>
                </div>
                <div className="flex items-center gap-3 text-sm">
                  <Phone size={14} className="text-sky-400" />
                  <DynamicCompanyInfo type="phone">
                    {(companyInfo: any) => <span className="text-slate-400">{companyInfo?.contact?.phone || '+1 (555) 123-4567'}</span>}
                  </DynamicCompanyInfo>
                </div>
                <div className="flex items-center gap-3 text-sm">
                  <MapPin size={14} className="text-sky-400" />
                  <DynamicCompanyInfo type="address">
                    {(companyInfo: any) => <span className="text-slate-400">{companyInfo?.address?.fullAddress || '123 Tech Street'}</span>}
                  </DynamicCompanyInfo>
                </div>
              </div>
            ) : (
              <div className="space-y-2.5">
                <div className="flex items-center gap-3 text-sm"><Building size={14} className="text-sky-400" /><span className="text-slate-400">Enterprise CRM Platform</span></div>
                <div className="flex items-center gap-3 text-sm"><Users size={14} className="text-green-400" /><span className="text-slate-400">Multi-Department Management</span></div>
                <div className="flex items-center gap-3 text-sm"><Shield size={14} className="text-purple-400" /><span className="text-slate-400">Secure & Compliant</span></div>
                {user && (
                  <div className="mt-3 pt-3 border-t border-slate-800 text-xs text-slate-500">
                    Logged in as <span className="text-sky-400">{user?.name}</span> • {user?.type}
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Footer Links */}
          {footerSections.map((section, index) => (
            <div key={index}>
              <h3 className="text-sm font-semibold text-white mb-4 uppercase tracking-wider">{section.title}</h3>
              <ul className="space-y-2.5">
                {section.links.map((link, linkIndex) => (
                  <li key={linkIndex}>
                    <Link href={link.href} className="text-slate-400 hover:text-sky-400 transition-colors duration-200 text-sm">
                      {link.name}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        {/* Newsletter / CTA */}
        {!isCRMPage && (
          <div className="bg-slate-800/50 backdrop-blur-sm rounded-2xl p-6 lg:p-8 mb-10 border border-slate-700/50">
            <div className="flex flex-col md:flex-row items-center justify-between gap-4">
              <div>
                <h3 className="text-lg font-bold mb-1">Stay Updated</h3>
                <p className="text-slate-400 text-sm">Latest tech insights and product updates</p>
              </div>
              <div className="flex gap-2 w-full md:w-auto">
                <input
                  type="email"
                  placeholder="Enter your email"
                  className="flex-1 md:w-64 px-4 py-2.5 bg-slate-900 border border-slate-700 rounded-xl text-sm text-white placeholder-slate-500 focus:ring-2 focus:ring-sky-500 focus:border-transparent transition-all"
                />
                <motion.button 
                  whileHover={{ scale: 1.02 }} 
                  whileTap={{ scale: 0.98 }}
                  className="px-5 py-2.5 bg-gradient-to-r from-sky-500 to-blue-600 text-white rounded-xl text-sm font-medium shadow-lg shadow-sky-500/20 hover:shadow-xl transition-all"
                >
                  Subscribe
                </motion.button>
              </div>
            </div>
          </div>
        )}

        {/* Bottom bar */}
        <div className="border-t border-slate-800 pt-6 flex flex-col md:flex-row justify-between items-center gap-4">
          <DynamicCompanyInfo type="companyName">
            {(companyInfo: any) => (
              <p className="text-slate-500 text-xs">&copy; {currentYear} {companyInfo?.companyName || 'Company'}. All rights reserved.</p>
            )}
          </DynamicCompanyInfo>

          <div className="flex items-center gap-5 text-xs">
            {isCRMPage ? (
              <>
                <Link href="/crm/help/privacy" className="text-slate-500 hover:text-sky-400 transition-colors">Privacy</Link>
                <Link href="/crm/help/compliance" className="text-slate-500 hover:text-sky-400 transition-colors">Compliance</Link>
              </>
            ) : (
              <>
                <Link href="/privacy-policy" className="text-slate-500 hover:text-sky-400 transition-colors">Privacy Policy</Link>
                <Link href="/terms-of-service" className="text-slate-500 hover:text-sky-400 transition-colors">Terms</Link>
                <Link href="/cookie-policy" className="text-slate-500 hover:text-sky-400 transition-colors">Cookies</Link>
              </>
            )}
          </div>

          <div className="flex items-center gap-3">
            {/* Social Icons */}
            <DynamicCompanyInfo type="social">
              {(companyInfo: any) => {
                const platforms = [
                  { name: 'Facebook', url: companyInfo?.socialMedia?.facebook, icon: <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24"><path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/></svg> },
                  { name: 'LinkedIn', url: companyInfo?.socialMedia?.linkedin, icon: <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24"><path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/></svg> },
                  { name: 'Instagram', url: companyInfo?.socialMedia?.instagram, icon: <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24"><path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z"/></svg> },
                  { name: 'YouTube', url: companyInfo?.socialMedia?.youtube, icon: <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24"><path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/></svg> },
                  { name: 'GitHub', url: companyInfo?.socialMedia?.github, icon: <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24"><path d="M12 0c-6.626 0-12 5.373-12 12 0 5.302 3.438 9.8 8.207 11.387.599.111.793-.261.793-.577v-2.234c-3.338.726-4.033-1.416-4.033-1.416-.546-1.387-1.333-1.756-1.333-1.756-1.089-.745.083-.729.083-.729 1.205.084 1.839 1.237 1.839 1.237 1.07 1.834 2.807 1.304 3.492.997.107-.775.418-1.305.762-1.604-2.665-.305-5.467-1.334-5.467-5.931 0-1.311.469-2.381 1.236-3.221-.124-.303-.535-1.524.117-3.176 0 0 1.008-.322 3.301 1.23.957-.266 1.983-.399 3.003-.404 1.02.005 2.047.138 3.006.404 2.291-1.552 3.297-1.23 3.297-1.23.653 1.653.242 2.874.118 3.176.77.84 1.235 1.911 1.235 3.221 0 4.609-2.807 5.624-5.479 5.921.43.372.823 1.102.823 2.222v3.293c0 .319.192.694.801.576 4.765-1.589 8.199-6.086 8.199-11.386 0-6.627-5.373-12-12-12z"/></svg> },
                ]
                const active = platforms.filter(p => p.url?.trim())
                if (!active.length) return null
                return (
                  <div className="flex gap-2">
                    {active.map((social, i) => (
                      <a key={i} href={social.url} target="_blank" rel="noopener noreferrer" aria-label={social.name}
                        className="w-8 h-8 bg-slate-800 rounded-lg flex items-center justify-center text-slate-400 hover:text-white hover:bg-sky-500 transition-all duration-300">
                        {social.icon}
                      </a>
                    ))}
                  </div>
                )
              }}
            </DynamicCompanyInfo>

            {/* Scroll to top */}
            <motion.button
              onClick={scrollToTop}
              whileHover={{ scale: 1.1, y: -2 }}
              whileTap={{ scale: 0.9 }}
              className="w-8 h-8 bg-gradient-to-br from-sky-500 to-blue-600 rounded-lg flex items-center justify-center text-white shadow-lg shadow-sky-500/20 hover:shadow-xl transition-all"
              aria-label="Scroll to top"
            >
              <ArrowUp size={14} />
            </motion.button>
          </div>
        </div>
      </div>
    </footer>
  )
}
